import { useState, useEffect, useRef } from "react";
import {
  Chip,
  Collapse,
  Group,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import {
  toIsoTimestamp,
  toValidationEventKind,
  useValidationEvents,
  VALIDATION_EVENT_KIND_COLORS,
  VALIDATION_EVENT_KINDS,
} from "./validation.service";
import type { ValidationEventKind } from "./validation.service";

const FEED_OPEN_STORAGE_KEY = "validation-event-feed-open";

/**
 * Event kinds users can toggle in the live feed. `ASSERTION` is intentionally
 * omitted — those are surfaced via {@link ValidationInvariantChecklist}.
 */
const FILTERABLE_KINDS: readonly ValidationEventKind[] =
  VALIDATION_EVENT_KINDS.filter((k) => k !== "ASSERTION");

interface EventItem {
  readonly id: string;
  readonly sequence: number;
  readonly timestamp: string;
  readonly kind: ValidationEventKind;
  readonly summary: string;
  readonly ruleId?: string | null;
}

interface ValidationEventFeedProps {
  readonly jobId: string;
}

function readInitialOpenState(): boolean {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(FEED_OPEN_STORAGE_KEY) === "true"
      : false;
  } catch {
    return false;
  }
}

function ValidationEventFeed(props: ValidationEventFeedProps) {
  const [open, setOpen] = useState<boolean>(readInitialOpenState);
  const [enabled, setEnabled] = useState<Set<ValidationEventKind>>(
    () => new Set<ValidationEventKind>(FILTERABLE_KINDS),
  );
  const [events, setEvents] = useState<readonly EventItem[]>([]);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const autoScrollRef = useRef<boolean>(true);

  const { data } = useValidationEvents(props.jobId);

  // Reset the buffered feed whenever the job changes so stale events from the
  // previous run never bleed into the new panel.
  useEffect(() => {
    seenIdsRef.current = new Set();
    setEvents([]);
  }, [props.jobId]);

  useEffect(() => {
    const payload = data?.validationEvents;
    if (!payload) return;
    const kind = toValidationEventKind(payload.kind);
    if (!kind) return;
    if (seenIdsRef.current.has(payload.id)) return;
    seenIdsRef.current.add(payload.id);
    const next: EventItem = {
      id: payload.id,
      sequence: payload.sequence,
      timestamp: toIsoTimestamp(payload.timestamp),
      kind,
      summary: payload.summary,
      ruleId: payload.ruleId,
    };
    setEvents((prev) => [...prev, next]);
  }, [data]);

  useEffect(() => {
    try {
      window.localStorage.setItem(FEED_OPEN_STORAGE_KEY, String(open));
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [open]);

  useEffect(() => {
    if (!autoScrollRef.current || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [events]);

  function onScrollPositionChange(position: { x: number; y: number }): void {
    const viewport = scrollRef.current;
    if (!viewport) return;
    const nearBottom =
      viewport.scrollHeight - position.y - viewport.clientHeight < 40;
    autoScrollRef.current = nearBottom;
  }

  function toggleKind(kind: ValidationEventKind): void {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) {
        next.delete(kind);
      } else {
        next.add(kind);
      }
      return next;
    });
  }

  const filtered = events.filter((e) => enabled.has(e.kind));

  return (
    <Stack gap="xs">
      <UnstyledButton onClick={() => setOpen(!open)}>
        <Group gap="xs">
          {open ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          <Text size="sm" fw={500}>
            Live events ({events.length})
          </Text>
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Stack gap="xs">
          <Group gap="xs">
            {FILTERABLE_KINDS.map((kind) => (
              <Chip
                key={kind}
                size="xs"
                checked={enabled.has(kind)}
                onChange={() => toggleKind(kind)}
                variant="light"
              >
                {kind}
              </Chip>
            ))}
          </Group>
          <ScrollArea
            h={200}
            viewportRef={scrollRef}
            onScrollPositionChange={onScrollPositionChange}
          >
            <Stack gap={2}>
              {filtered.map((e) => (
                <Text size="xs" key={e.id} ff="monospace">
                  <span style={{ color: "var(--mantine-color-dimmed)" }}>
                    #{e.sequence}
                  </span>{" "}
                  <span style={{ color: VALIDATION_EVENT_KIND_COLORS[e.kind] }}>
                    [{e.kind}]
                  </span>{" "}
                  {/* Server-supplied text rendered as a text node — React escapes it. */}
                  {e.summary}
                </Text>
              ))}
            </Stack>
          </ScrollArea>
        </Stack>
      </Collapse>
    </Stack>
  );
}

export { ValidationEventFeed };
export type { ValidationEventFeedProps };
